/**
 * Represents a validation-specific error with an associated error code and data payload.
 * 
 * @extends Error
 */
export class ValidationError extends Error {
  /**
   * A unique code representing the specific validation error.
   */
  code: string | unknown

  /**
   * Additional data providing context for the error.
   */
  data: any

  /**
   * Constructs a new `ValidationError` instance.
   * 
   * @param message - A descriptive error message.
   * @param code - A string or unknown value identifying the error code.
   * @param data - Optional additional data related to the error.
   */
  constructor(message: any, code: string | unknown, data: any) {
    super(message);
    this.name = 'ValidationError';
    this.code = code;
    this.data = data;
  }
}


/**
 * Checks an HTTP or object-based response for known error formats and throws appropriate exceptions.
 * 
 * This function is designed to handle different types of error responses consistently:
 * - If the response has a `status` and `title`, it throws a generic `Error` using `title`.
 * - If the response has both `error` and `code`, it throws a `ValidationError`.
 * - If the response has only `error`, it throws a generic `Error`.
 * - Otherwise, it returns the response as-is.
 * 
 * @template T - The response type, which may be an HTTP `Response` object, string, or a plain object.
 * @param response - The response object or value to check for errors.
 * @returns A promise resolving with the original response if no errors are found.
 * @throws {ValidationError} If a validation error is detected.
 * @throws {Error} If a general error is detected in the response.
 * 
 * @example
 * ```ts
 * const res = await fetch('/api/data');
 * try {
 *   await checkErrors(res);
 * } catch (err) {
 *   if (err instanceof ValidationError) {
 *     console.error('Validation failed:', err.code, err.data);
 *   } else {
 *     console.error('Unexpected error:', err.message);
 *   }
 * }
 * ```
 */
async function checkErrors<T extends (Response | string | object & {
  status?: string
  title?: string
  error?: string | Error
  code?: string
  data?: any
})>(response: T): Promise<T> {
  const data = await ((response instanceof Response) ? response.json() : response);
  const { status, title, error, code, data: errorData } = data;

  if (status && title){
    throw new Error(title);
  } else if (error && code) {
    throw new ValidationError(error, code, errorData);
  } else if (error){
    throw new Error(error);
  } else {
    return response;
  }
}


export default checkErrors;